
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, ExternalLink, FileText } from 'lucide-react';
import { WorkflowStep } from '../types';
import { exportDocument } from '../utils/documentExport';
import { toast } from 'react-hot-toast';

interface SurveyDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  steps: WorkflowStep[];
  consolidatedReport: string | null;
  campaignName: string;
}

const SurveyDownloadDialog: React.FC<SurveyDownloadDialogProps> = ({
  isOpen,
  onClose,
  steps,
  consolidatedReport,
  campaignName
}) => {
  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);

  // Filter for research reports and add consolidated report
  const availableReports = [
    ...steps.filter(step => 
      step.stage === 'research' && 
      step.status === 'completed' && 
      step.result
    ).map(step => ({
      id: `step-${step.id}`,
      title: step.title,
      description: step.description,
      content: step.result!,
      type: 'research' as const
    })),
    ...(consolidatedReport ? [{
      id: 'consolidated',
      title: 'Consolidated Campaign Analysis Report',
      description: 'Complete analysis with executive summary and campaign recommendations',
      content: consolidatedReport,
      type: 'consolidated' as const
    }] : [])
  ];

  const handleReportToggle = (reportId: string) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(reportId)) {
      newSelected.delete(reportId);
    } else {
      newSelected.add(reportId);
    }
    setSelectedReports(newSelected);
  };

  const handleDownloadReport = async (report: typeof availableReports[0]) => {
    try {
      setIsDownloading(true);
      
      // Create a temporary container for the report content
      const container = document.createElement('div');
      container.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="font-size: 24px; margin-bottom: 20px;">${report.title}</h1>
          <div style="white-space: pre-wrap; line-height: 1.6;">${report.content}</div>
        </div>
      `;

      const fileName = `${campaignName.toLowerCase().replace(/\s+/g, '-')}-${report.title.toLowerCase().replace(/\s+/g, '-')}`;
      await exportDocument(container, report.content, 'pdf', fileName);
      
      toast.success(`${report.title} downloaded successfully`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedReports.size === 0) {
      toast.error('Please select at least one report to download');
      return;
    }

    try {
      setIsDownloading(true);
      
      for (const reportId of selectedReports) {
        const report = availableReports.find(r => r.id === reportId);
        if (report) {
          await handleDownloadReport(report);
        }
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleContinueToSurvey = () => {
    window.open('https://poll-assistant.reveille.net.au', '_blank');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Download Research Reports Before Creating Survey
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            We recommend downloading your research reports before creating your survey. 
            These reports contain valuable insights that can help inform your survey questions.
          </p>

          <div className="space-y-3">
            <h3 className="font-medium">Available Reports:</h3>
            {availableReports.map((report) => (
              <div key={report.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id={report.id}
                      checked={selectedReports.has(report.id)}
                      onChange={() => handleReportToggle(report.id)}
                      className="mt-1"
                    />
                    <div>
                      <label 
                        htmlFor={report.id}
                        className="font-medium cursor-pointer"
                      >
                        {report.title}
                      </label>
                      <p className="text-sm text-muted-foreground">
                        {report.description}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadReport(report)}
                    disabled={isDownloading}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {availableReports.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No research reports available for download.
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button
              onClick={handleDownloadSelected}
              disabled={selectedReports.size === 0 || isDownloading}
              className="flex-1"
            >
              <Download className="w-4 h-4 mr-2" />
              {isDownloading ? 'Downloading...' : `Download Selected (${selectedReports.size})`}
            </Button>
            
            <Button
              onClick={handleContinueToSurvey}
              variant="outline"
              className="flex-1"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Continue to Survey Tool
            </Button>
          </div>

          <Button
            onClick={onClose}
            variant="ghost"
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SurveyDownloadDialog;
